<template>
  <div data-dev="builder-page">
    <a
      href="#main-content"
      class="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:bg-white focus:text-black focus:p-2 focus:z-[100000]"
      >Skip to main content</a
    >
    <header class="sr-only" role="banner"></header>
    <main id="main-content" role="main" tabindex="-1">
      <div v-if="content" :class="[{ 'container-parent': !applyFullPage }]">
        <div class="container-child">
          <div
            ref="box_ani"
            class="container-sub-child"
            :class="[{ '!font-gotham box-ani': slug == 'healthy-coffee-23' }]"
          >
            <Content
              v-if="
                content?.content &&
                builderComponents &&
                builderComponents.length > 0
              "
              model="page"
              :content="content.content"
              :api-key="BUILDER_API_KEY"
              :customComponents="builderComponents"
            />
          </div>
        </div>
      </div>
    </main>
    <footer class="sr-only" role="contentinfo"></footer>
    <ContextMenu />
  </div>
</template>
<script setup>
import { Content, fetchOneEntry, isEditing } from "@builder.io/sdk-vue";

useHead({
  layout: "default",
  title: "Healthy Coffee 2023",
  description:
    "Lifeboost Coffee is the healthiest coffee on the planet. Our coffee is 100% certified organic, single origin, shade grown, fairly traded, and small batch roasted in the USA.",
  link: [
    {
      rel: "preload",
      as: "font",
      crossorigin: "",
      type: "font/woff2",
      href: "/assets/fonts/Gotham-Book.woff2",
    },
    {
      rel: "preload",
      as: "font",
      crossorigin: "",
      type: "font/ttf",
      href: "/assets/fonts/GothamBold.ttf",
    },
    {
      rel: "preload",
      as: "font",
      crossorigin: "",
      type: "font/woff2",
      href: "/assets/fonts/wigrumweb-medium.woff2",
    },
    {
      rel: "preload",
      as: "font",
      crossorigin: "",
      type: "font/woff2",
      href: "/assets/fonts/Roboto-Italic.woff2",
    },
    {
      rel: "preload",
      as: "font",
      crossorigin: "",
      type: "font/woff2",
      href: "/assets/fonts/Roboto-Regular.woff2",
    },
  ],
});

const headers = useRequestHeaders();
const config = useRuntimeConfig();
const BUILDER_API_KEY = "16799f3421e14584be07999a4c604287";
const box_ani = ref(null);
const nuxtApp = useNuxtApp();

// const isEditing = headers["sec-fetch-dest"] ?  true : false; // This is a hack to get the isEditing state from the server
const route = useRoute();
const slug = route.params.slug;

// Helper function to extract component names from Builder.io content
function extractComponentNames(obj, depth = 0) {
  const names = new Set(); // Use Set to avoid duplicates

  if (depth > 50) return Array.from(names); // Increased depth limit

  if (Array.isArray(obj)) {
    for (const item of obj) {
      const extracted = extractComponentNames(item, depth + 1);
      extracted.forEach((name) => names.add(name));
    }
  } else if (obj && typeof obj === "object") {
    // Extract friendly names from Builder.io component structure
    if (obj.component?.name) {
      names.add(obj.component.name);
    }
    // Also check for @type === "@builder.io/sdk:Element" with component property
    if (obj["@type"] === "@builder.io/sdk:Element" && obj.component?.name) {
      names.add(obj.component.name);
    }
    // Recursively check all properties
    for (const key of Object.keys(obj)) {
      if (key !== "parent" && key !== "_parent") {
        // Avoid circular references
        const extracted = extractComponentNames(obj[key], depth + 1);
        extracted.forEach((name) => names.add(name));
      }
    }
  }

  return Array.from(names);
}

// First, fetch the page content to get required component names
const { data: initialContent } = await useAsyncData(
  `initialContent-${route.path}`,
  () =>
    fetchOneEntry({
      model: "page",
      apiKey: BUILDER_API_KEY,
      userAttributes: { urlPath: route.path },
    })
);

// Extract component names from the content
const requiredComponentNames = initialContent.value
  ? extractComponentNames(initialContent.value.data)
  : [];

// Components that should always be loaded in development, even if not found in initial content
// This is useful for split testing components or dynamic content
const forcedComponents = ["SwatchesTest-9", "SwatchesTest8-5"];

// Dynamically load only the components needed for this page
// In development, load only required components + forced components for performance.
// In production (or editing), load all components to support split testing and dynamic content.
const componentsToLoad =
  import.meta.dev && !isEditing()
    ? [...new Set([...requiredComponentNames, ...forcedComponents])]
    : undefined;

const builderComponentsResult = useBuilderComponents(componentsToLoad);

// Wait for components to be ready
await builderComponentsResult.ready;

const builderComponents = builderComponentsResult.components;

//console.log('Loaded builder components:', builderComponents.value);

// Now fetch the full page data with the resolved components
const { data: content } = await useAsyncData(
  `builderData-page-${route.path}`,
  () =>
    nuxtApp.$fetchBuilderPage(
      "page",
      BUILDER_API_KEY,
      route.path,
      isEditing(),
      builderComponents.value
    )
);

//console.log('Page content:', content.value);
//console.log('Page content blocks:', content.value?.content?.data?.blocks);
//console.log('Components passed to Content:', builderComponents.value);

const componentMetadata = content.value?.components || [];

const componentModules = import.meta.glob("~/components/**/*.vue", {
  eager: false,
});

const registry = {};
// Create async components on the client side using friendly names
const REGISTERED_COMPONENTS = componentMetadata.map((meta) => ({
  ...meta,
  component: defineAsyncComponent(
    registry[meta.name] || (() => Promise.resolve({}))
  ),
}));

if (content.value === null) {
  useBugsnag().notify(new Error("404 Triggered: Page Not Found", slug));
  throw createError({
    statusCode: 404,
    statusMessage: "Page Not Found",
    fatal: true,
  });
}

const applyFullPage = computed(
  () => content?.value?.content?.data?.fullWidthPage === "on"
);

if (content && content?.value?.content?.data) {
  useSeoMeta({
    title: () => content.value.content.data.title,
    description: () => content.value.content.data.description,
    image: () => content.value.content.data.metaImage,
  });

  onMounted(async () => {
    box_ani?.value?.classList?.add("shadow");

    const builderData = await fetchOneEntry({
      model: "page",
      apiKey: BUILDER_API_KEY,
      userAttributes: {
        urlPath: `/${slug}`,
      },
    });

    const GTMTrack = () => {
      const data = builderData;
      const eventData = {
        experiment_name: data.name,
        variation_name: data.testVariationName,
        variation_id: data.testVariationId,
      };

      window.dataLayer = window.dataLayer || [];

      window.dataLayer?.push({
        event: "builderio_experiment",
        ...eventData,
      });
    };

    if (
      builderData?.hasOwnProperty("testVariationId") &&
      builderData.meta.winningTest === null
    ) {
      GTMTrack();
    }
  });
}
</script>

<style lang="postcss" scoped>
.font-gotham {
  font-family: "Gotham", sans-serif;
}
#fader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999999;
  pointer-events: none;
  background: #fff;
  animation-duration: 0.3s;
  animation-timing-function: ease-in-out;
  opacity: 1;
}

@keyframes fade-out {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

#fader.fade-out {
  animation-name: fade-out;
  opacity: 0;
}

#fader.fade-in {
  animation-name: fade-in;
  opacity: 1;
}

.box-ani {
  box-shadow: 0;
  transition: box-shadow 1s;
}

.box-ani.shadow {
  box-shadow: 0 0 27px rgba(11, 11, 12, 0.39);
}

.container-parent {
  @apply bg-bgGrayBody md-max:pb-12;
}

.container-parent .container-child {
  @apply w-full px-8 pb-0 m-auto max-w-7xl sm-max:px-5 lg:pb-16;
}

.container-parent .container-sub-child {
  @apply relative w-full max-w-6xl m-auto bg-white text-21;
}
</style>
